import { useState, useMemo, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useRandevular, createRandevu, updateRandevu, deleteRandevu, Randevu } from './hooks/useRandevular';
import HaftalikTakvim from './components/HaftalikTakvim';
import { ISLEM_TURLERI } from '@/pages/servis-kayitlari/hooks/useServisKayitlari';

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

const islemRenk: Record<string, string> = {
  'Filtre Bakımı – Dörtlü': 'bg-sky-100 text-sky-700',
  'Filtre Bakımı – Beşli': 'bg-indigo-100 text-indigo-700',
  'Genel Bakım': 'bg-emerald-100 text-emerald-700',
  'Yeni Montaj': 'bg-violet-100 text-violet-700',
  'Bina Girişi': 'bg-teal-100 text-teal-700',
  'Bina Girişi Bakım': 'bg-cyan-100 text-cyan-700',
  'Taşıma': 'bg-orange-100 text-orange-700',
  'Diğer': 'bg-gray-100 text-gray-700',
};

export default function ServisPlanlamaPage() {
  const { randevular, loading, error, refetch } = useRandevular();
  const [weekStart, setWeekStart] = useState<Date>(getMonday(new Date()));
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultDate, setDefaultDate] = useState('');
  const [editData, setEditData] = useState<Randevu | null>(null);
  const [detailApt, setDetailApt] = useState<Randevu | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [musteriler, setMusteriler] = useState<any[]>([]);

  // Form state for modal
  const [form, setForm] = useState({
    musteriId: '', tarih: '', saat: '09:00', islemTuru: 'Filtre Bakımı – Dörtlü', notlar: '',
  });

  useEffect(() => {
    supabase.from('sadem_musteriler').select('id, ad, soyad, telefon, ilce').eq('aktif', true).eq('silindi', false).order('ad').then(({ data }) => {
      if (data) setMusteriler(data);
    }).catch(() => {});
  }, []);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d;
  }, [weekStart]);

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d);
  };

  const goToday = () => setWeekStart(getMonday(new Date()));

  const handleDayClick = (dateStr: string) => {
    setDefaultDate(dateStr);
    setEditData(null);
    setForm({
      musteriId: '', tarih: dateStr, saat: '09:00', islemTuru: 'Filtre Bakımı – Dörtlü', notlar: '',
    });
    setModalOpen(true);
  };

  const handleAptClick = (apt: Randevu) => {
    setDetailApt(apt);
  };

  const handleAptEdit = (apt: Randevu) => {
    setEditData(apt);
    setForm({
      musteriId: apt.musteri_id, tarih: apt.servis_tarihi, saat: apt.servis_saati,
      islemTuru: apt.islem_turu, notlar: apt.servis_notu,
    });
    setDetailApt(null);
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.musteriId) return;
    setSaving(true);
    try {
      if (editData) {
        await updateRandevu(editData.id, {
          servis_tarihi: form.tarih,
          servis_saati: form.saat,
          islem_turu: form.islemTuru,
          servis_notu: form.notlar,
        });
      } else {
        await createRandevu({
          musteri_id: form.musteriId,
          servis_tarihi: form.tarih,
          servis_saati: form.saat,
          islem_turu: form.islemTuru,
          servis_notu: form.notlar,
        });
      }
      setModalOpen(false);
      setEditData(null);
      refetch();
    } catch (err: any) {
      alert('Randevu kaydedilemedi. Lütfen tekrar deneyiniz.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRandevu(id);
      setDeleteConfirm(null);
      setDetailApt(null);
      refetch();
    } catch {
      alert('Bu işlem için yetkiniz bulunmuyor.');
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();

  const todayApts = randevular.filter(a => a.servis_tarihi === todayStr).sort((a, b) => a.servis_saati.localeCompare(b.servis_saati));
  const tomorrowApts = randevular.filter(a => a.servis_tarihi === tomorrowStr).sort((a, b) => a.servis_saati.localeCompare(b.servis_saati));

  const weekApts = randevular.filter(a => {
    const d = new Date(a.servis_tarihi);
    d.setHours(0, 0, 0, 0);
    return d >= weekStart && d <= weekEnd;
  });

  const isCurrentWeek = getMonday(new Date()).getTime() === weekStart.getTime();

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-200 bg-white';
  const saatler = ['08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00','17:30','18:00'];

  if (loading) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line animate-spin text-3xl text-sky-500"></i>
        <p className="text-sm text-gray-500 mt-3">Randevular yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-center">
        <p className="text-sm text-red-700 font-medium">Randevular yüklenemedi.</p>
        <button onClick={refetch} className="mt-3 text-sm text-red-600 font-semibold cursor-pointer">Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={prevWeek} className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer text-gray-600 transition-colors">
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <div className="text-center">
            <p className="text-base font-bold text-gray-800">
              {weekStart.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })}
              {' — '}
              {weekEnd.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
            {isCurrentWeek && <span className="text-xs text-sky-600 font-semibold">Bu Hafta</span>}
          </div>
          <button onClick={nextWeek} className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer text-gray-600 transition-colors">
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
          {!isCurrentWeek && (
            <button onClick={goToday} className="text-xs bg-sky-50 hover:bg-sky-100 text-sky-700 font-semibold px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">Bugüne Dön</button>
          )}
        </div>
        <button onClick={() => { setEditData(null); setForm({ musteriId: '', tarih: todayStr, saat: '09:00', islemTuru: 'Filtre Bakımı – Dörtlü', notlar: '' }); setModalOpen(true); }}
          className="flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold px-4 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-add-line"></i> Yeni Randevu
        </button>
      </div>

      {/* Bugün + Yarın Özet */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { label: 'Bugün', dateStr: todayStr, list: todayApts, color: 'border-sky-200 bg-sky-50/50' },
          { label: 'Yarın', dateStr: tomorrowStr, list: tomorrowApts, color: 'border-gray-200 bg-white' },
        ].map(({ label, dateStr, list, color }) => (
          <div key={label} className={`rounded-xl border p-4 ${color}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-700">{label}</h3>
              <span className="text-xs bg-white border border-gray-200 text-gray-600 font-semibold px-2 py-1 rounded-full">{list.length} randevu</span>
            </div>
            {list.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">Bu tarihte planlanmış servis bulunmuyor.</p>
            ) : (
              <div className="space-y-2">
                {list.map(a => (
                  <div key={a.id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100">
                    <span className="text-xs font-bold text-sky-600 w-12 flex-shrink-0">{a.servis_saati}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{a.musteri_ad} {a.musteri_soyad}</p>
                      <p className="text-xs text-gray-400 truncate">{a.islem_turu}</p>
                    </div>
                    <button onClick={() => handleAptClick(a)} className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-sky-500 cursor-pointer">
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => handleDayClick(dateStr)} className="w-full mt-3 text-xs text-sky-600 hover:text-sky-800 font-medium py-1.5 border border-dashed border-sky-200 rounded-lg cursor-pointer hover:bg-sky-50 transition-colors">
              + Randevu Ekle
            </button>
          </div>
        ))}
      </div>

      {/* Haftalık Takvim */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-700">Haftalık Plan</h3>
          <span className="text-xs text-gray-400">{weekApts.length} randevu bu hafta • Güne tıklayarak randevu ekleyin</span>
        </div>
        <HaftalikTakvim
          weekStart={weekStart}
          appointments={randevular.map(r => ({
            id: r.id,
            musteriId: r.musteri_id,
            musteriAdi: `${r.musteri_ad} ${r.musteri_soyad}`,
            telefon: r.musteri_telefon,
            adres: r.musteri_adres,
            tarih: r.servis_tarihi,
            saat: r.servis_saati,
            islemTuru: r.islem_turu,
            notlar: r.servis_notu,
            renk: r.renk,
          }))}
          onDayClick={handleDayClick}
          onAptClick={(apt: any) => {
            const real = randevular.find(r => r.id === apt.id);
            if (real) handleAptClick(real);
          }}
          onAptDelete={(id) => setDeleteConfirm(id)}
        />
      </div>

      {/* Detay Modal */}
      {detailApt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">Randevu Detayı</h3>
              <button onClick={() => setDetailApt(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-3-line text-sky-600"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{detailApt.musteri_ad} {detailApt.musteri_soyad}</p>
                  <p className="text-xs text-gray-400">{detailApt.musteri_telefon}</p>
                </div>
              </div>
              <div className="space-y-2 bg-slate-50 rounded-xl p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tarih</span>
                  <span className="font-semibold text-gray-800">{detailApt.servis_tarihi ? new Date(detailApt.servis_tarihi).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' }) : '-'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Saat</span>
                  <span className="font-bold text-sky-600">{detailApt.servis_saati}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">İşlem</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${islemRenk[detailApt.islem_turu] || 'bg-gray-100 text-gray-600'}`}>{detailApt.islem_turu}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Adres</span>
                  <span className="font-medium text-gray-700 text-right max-w-[180px] text-xs">{detailApt.musteri_adres}</span>
                </div>
                {detailApt.servis_notu && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Not</span>
                    <span className="font-medium text-gray-700 text-xs text-right max-w-[180px]">{detailApt.servis_notu}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <a href={`tel:${detailApt.musteri_telefon}`} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-phone-line"></i> Ara
                </a>
                <button onClick={() => handleAptEdit(detailApt)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-sky-50 hover:bg-sky-100 text-sky-700 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-pencil-line"></i> Düzenle
                </button>
                <button onClick={() => { setDeleteConfirm(detailApt.id); setDetailApt(null); }} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors">
                  <i className="ri-delete-bin-line"></i> Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-gray-800 text-center">Randevu Silinsin mi?</h3>
            <p className="text-sm text-gray-500 text-center mt-1">Bu işlem geri alınamaz.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 cursor-pointer text-sm whitespace-nowrap">İptal</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">Evet, Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Randevu Ekle/Düzenle Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-base font-bold text-gray-800">{editData ? 'Randevuyu Düzenle' : 'Yeni Randevu Oluştur'}</h3>
              <button onClick={() => { setModalOpen(false); setEditData(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Müşteri *</label>
                <select required className={inputCls} value={form.musteriId} onChange={e => setForm(p => ({ ...p, musteriId: e.target.value }))}>
                  <option value="">Müşteri seçiniz...</option>
                  {musteriler.map(m => <option key={m.id} value={m.id}>{m.ad} {m.soyad} — {m.ilce}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Tarih *</label>
                  <input required type="date" className={inputCls} value={form.tarih} onChange={e => setForm(p => ({ ...p, tarih: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Saat *</label>
                  <select required className={inputCls} value={form.saat} onChange={e => setForm(p => ({ ...p, saat: e.target.value }))}>
                    {saatler.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">İşlem Türü</label>
                <div className="grid grid-cols-2 gap-2">
                  {ISLEM_TURLERI.map(t => (
                    <button key={t} type="button" onClick={() => setForm(p => ({ ...p, islemTuru: t }))}
                      className={`text-xs font-semibold py-2.5 px-3 rounded-lg border transition-all cursor-pointer text-left whitespace-nowrap
                        ${form.islemTuru === t ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600 border-gray-200 hover:border-sky-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Not</label>
                <textarea className={`${inputCls} resize-none`} rows={2} value={form.notlar} onChange={e => setForm(p => ({ ...p, notlar: e.target.value }))} placeholder="Randevu ile ilgili not..." maxLength={200} />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => { setModalOpen(false); setEditData(null); }} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl cursor-pointer text-sm whitespace-nowrap transition-colors disabled:opacity-50">
                  {saving ? 'Kaydediliyor...' : editData ? 'Güncelle' : 'Randevu Ekle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}